import { OFFLINE_GAMES_ENABLED } from "./services/offlineGames/featureEnabled";
import React, { Suspense, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux"; 
import loadable from "@loadable/component";   
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";

import "./App.css"; 
import AppRoutes from "./routes";
import MessagePopup from './components/UI/MessagePopup'

import { LoadHomDataAction } from "./redux/actions/homeActions";
import { LogoutAction, SilentLoginAction, SILENT_LOGIN_FAILED } from "./redux/actions/authActions";
import { getAllCategoryAction } from "./redux/actions/categoryActions";
import { checkCookie, checkFunctionRateLimit } from "./utils/functions";
import useOfflineGamesLifecycle from "./services/offlineGames/useOfflineGamesLifecycle";
import useOnlineStatus from "./services/offlineGames/useOnlineStatus";

const Loader = loadable(() => import("./components/UI/Loader"));
const Toast = loadable(() => import("./components/UI/Toast"));
const EngagingLoader = loadable(() => import("./components/UI/EngagingLoader"));
const OfflineNotice = loadable(() => import("./components/offline/OfflineNotice"));
const OfflineDownloadPrompt = loadable(() => import("./components/offline/OfflineDownloadPrompt"));

const App = () => {
	const HomeState = useSelector((state) => state.Home);
	const AuthState = useSelector((state) => state.Auth);
	const ToastState = useSelector((state) => state.Toast);
	const MessageState = useSelector((state) => state.Message);

	const { showMessagePopup,popupMessage,userMessagesList } = MessageState;
	const { loaded: homeLoaded } = HomeState;
	const { loading: authLoading,token,user } = AuthState;
	const { open,message,severity } = ToastState;
	const location = useLocation();
	useOfflineGamesLifecycle();
	const isOnline = useOnlineStatus();
	const navigate = useNavigate();

	let isLoggedIn = Boolean(user && token); 
   
	const dispatch = useDispatch();  
	const silentLoginStartedRef = useRef(false);
	const forceRefreshAttemptedRef = useRef(false);
	const anonymousDeepLinkRedirectRef = useRef(false);

	useEffect(() => {
		if (!("scrollRestoration" in window.history)) {
			return;
		}

		const previousScrollRestoration = window.history.scrollRestoration;
		window.history.scrollRestoration = "manual";

		return () => {
			window.history.scrollRestoration = previousScrollRestoration;
		};
		}, []);

	useEffect(() => {
		let cmpShadowRoot = null;
		let cmpShadowObserver = null;
		let privacyButtonStyle = null;

		const ensurePrivacyButtonIsHidden = () => {
			const nextShadowRoot = document.getElementById("usercentrics-root")?.shadowRoot;
			if (!nextShadowRoot) {
				return;
			}

			if (cmpShadowRoot !== nextShadowRoot) {
				cmpShadowObserver?.disconnect();
				cmpShadowRoot = nextShadowRoot;
				cmpShadowObserver = new MutationObserver(ensurePrivacyButtonIsHidden);
				cmpShadowObserver.observe(cmpShadowRoot, { childList: true, subtree: true });
			}

			privacyButtonStyle = cmpShadowRoot.querySelector("style[data-akedo-hide-privacy-button]");
			if (!privacyButtonStyle) {
				privacyButtonStyle = document.createElement("style");
				privacyButtonStyle.setAttribute("data-akedo-hide-privacy-button", "true");
				privacyButtonStyle.textContent = '[data-testid="uc-privacy-button"] { display: none !important; }';
				cmpShadowRoot.appendChild(privacyButtonStyle);
			}
		};

		const cmpHostObserver = new MutationObserver(ensurePrivacyButtonIsHidden);
		cmpHostObserver.observe(document.documentElement, { childList: true, subtree: true });
		ensurePrivacyButtonIsHidden();

		return () => {
			cmpHostObserver.disconnect();
			cmpShadowObserver?.disconnect();
			privacyButtonStyle?.remove();
		};
	}, []);

	useEffect(() => {
		const isDetailsDeepLink = /^\/details(?:-optimized)?\/[^/]+/.test(location.pathname);
		const isSettingsDeepLink = location.pathname === "/settings";

		if (anonymousDeepLinkRedirectRef.current || (!isDetailsDeepLink && !isSettingsDeepLink) || localStorage.getItem("token")) {
			return;
		}

		anonymousDeepLinkRedirectRef.current = true;
		navigate({ pathname: "/", search: location.search }, { replace: true, state: { from: location.pathname } });
	}, [location.pathname, location.search, navigate]);
	
	useEffect(() => { 
		if (!checkFunctionRateLimit()) {
			return;
		}
		// Discord sign-in returns to /discord/logincallback (DiscordCallback), not here.
		dispatch(getAllCategoryAction());
	}, [dispatch, homeLoaded, isLoggedIn]); 

	/**START: TO AUTO LOGOUT USER AFTER SPECIFIC TIME */
	useEffect(() => { 
		const sessionExists = checkCookie('sessionExists');
		const hasToken = localStorage.getItem('token');
		console.log('[Auth:boot] cookie=sessionExists:', Boolean(sessionExists), '| localStorage=token:', Boolean(hasToken));
		// Only force-logout when BOTH the cookie is gone AND there is no token.
		// If a token exists, SilentLoginSaga will send it to /app_login and the server
		// will return 401 if it has truly expired — that is the correct signal to clear it.
		// Removing a valid token here just because the cookie expired silently degrades
		// returning users to a new anonymous session and triggers the "Something went wrong" toast.
		if (!sessionExists && !hasToken) {
			console.warn('[Auth:boot] No session cookie and no token — dispatching LogoutAction');
			dispatch(LogoutAction()); 
		} else if (!sessionExists && hasToken) {
			console.log('[Auth:boot] Cookie missing but token present — letting /app_login validate the token');
		} else if (sessionExists && hasToken) {
			console.log('[Auth:boot] Cookie and token both present — user should be re-authenticated');
		}
	}, [dispatch]);
	/**END: TO AUTO LOGOUT USER AFTER SPECIFIC TIME */

	/**START: WALLET USER MIGRATION - convert wallet users to silent login */
	useEffect(() => {
		if (!homeLoaded) return;
		const hasWeb3Token = localStorage.getItem('web3-token');
		const hasToken = localStorage.getItem('token');
		if (hasWeb3Token && hasToken) {
			console.log('[App] Wallet user detected — migrating to silent login flow...');
			localStorage.removeItem('web3-token');
			// Re-authenticate via /app_login using their existing JWT (x-access-token).
			// Server returns same user data; isSilentLogin is set to true so they see Sync buttons.
			dispatch(SilentLoginAction());
		}
	}, [dispatch, homeLoaded]);
	/**END: WALLET USER MIGRATION */

	/**START: SILENT LOGIN - AUTO LOGIN ON APP INIT */
	useEffect(() => {
		// Only attempt anonymous login if:
		// 1. User is not already logged in (no token in Redux state or localStorage)
		// 2. Not processing Discord OAuth callback (no access_token in hash)
		const hasToken = localStorage.getItem('token');
		const hash = window.location.hash.slice(1);
		const params = new URLSearchParams(hash);
		const hasAccessTokenInHash = params.get('access_token');
		
		console.log('[Auth:silentLogin] gate check — redux token:', Boolean(token), '| localStorage token:', Boolean(hasToken), '| authLoading:', authLoading, '| homeLoaded:', homeLoaded, '| discordHash:', Boolean(hasAccessTokenInHash));
		if (token) {
			silentLoginStartedRef.current = false;
			return;
		}

		if (silentLoginStartedRef.current || authLoading || hasAccessTokenInHash) {
			return;
		}

		if (!hasToken) {
			console.log('[Auth:silentLogin] No login found — starting /app_login (new anonymous session)');
			silentLoginStartedRef.current = true;
			dispatch(SilentLoginAction());
		} else {
			// Returning user: a token is persisted in localStorage but the Redux session
			// has not been established yet for this app load. Validate it via /app_login
			// (SilentLoginSaga sends it as x-access-token). If the token has expired the
			// axios interceptor will call /refresh and store the new token, then retry.
			// Once SILENT_LOGIN_SUCCESS sets the Redux token this branch stops firing,
			// so there is no re-validation loop.
			console.log('[Auth:silentLogin] Existing token found — validating via /app_login');
			silentLoginStartedRef.current = true;
			dispatch(SilentLoginAction());
		}
	}, [authLoading, dispatch, homeLoaded, token]);
	/**END: SILENT LOGIN - AUTO LOGIN ON APP INIT */

	/**START: TEMP HACK — FORCE TOKEN REFRESH FOR ANY USER (REMOVE AFTER WEEKEND) */
	// Temporary: on each boot, if a token is present, hit /refresh once and persist
	// the returned token so every user gets a freshly minted token regardless of
	// whether a protected endpoint returned 401/403. Delete this whole effect to revert.
	useEffect(() => {
		if (!homeLoaded) return;
		if (forceRefreshAttemptedRef.current) return;
		const oldToken = localStorage.getItem('token');
		if (!oldToken) return;

		forceRefreshAttemptedRef.current = true;
		console.log('[Auth:TEMP forceRefresh] forcing /refresh for current token');
		fetch(`${import.meta.env.VITE_APP_BASE_URL}refresh`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'x-access-token': oldToken },
			body: '{}',
		})
			.then((res) => {
				if (res.ok) return res.json();
				const error = new Error(`refresh ${res.status}`);
				error.status = res.status;
				throw error;
			})
			.then((data) => {
				const nextToken = data?.accessToken || data?.token || data?.data?.accessToken;
				if (!nextToken) throw new Error('no accessToken in refresh response');
				localStorage.setItem('token', nextToken);
				dispatch({ type: 'TOKEN_REFRESHED', token: nextToken });
				console.log('[Auth:TEMP forceRefresh] token refreshed & stored');
			})
			.catch((err) => {
				console.warn('[Auth:TEMP forceRefresh] skipped:', err?.message);
				if ((err?.status === 401 || err?.status === 403) && token) {
					console.warn('[Auth:TEMP forceRefresh] stored token rejected — clearing and starting new silent login');
					localStorage.removeItem('token');
					localStorage.removeItem('web3-token');
					dispatch({ type: SILENT_LOGIN_FAILED });
					dispatch(SilentLoginAction());
				}
			});
	}, [dispatch, homeLoaded, token]);
	/**END: TEMP HACK — FORCE TOKEN REFRESH FOR ANY USER (REMOVE AFTER WEEKEND) */


	/**START: USED TO LOAD HOME PAGE AGAIN ON LOGIN LOGOUT */
	useEffect(()=>{
		if (!token) {
			return;
		}

		dispatch(LoadHomDataAction());
	},[dispatch,token])
	/**END: USED TO LOAD HOME PAGE AGAIN ON LOGIN LOGOUT */
	
	useEffect(() =>{},[showMessagePopup]);  

	useEffect(() => {
		const placeVersionText = () => {
			const versionText = document.getElementById("version-text");
			if (!versionText) {
				return;
			}

			const clientVersion = import.meta.env.VITE_APP_VERSION;
			const serverVersionText = versionText.textContent
				.replace(/\s*\|\s*(?:CLIENT:\s*)?[\d]+\.[\d]+\.[\d]+\s*$/i, "")
				.trim();
			const fullVersionText = `${serverVersionText} | ${clientVersion}`;
			if (versionText.textContent !== fullVersionText) {
				versionText.textContent = fullVersionText;
			}

			const footer = document.querySelector(".layoutFooter .innerFooter");
			if (!footer) {
				versionText.hidden = true;
				return;
			}

			versionText.hidden = false;
			versionText.className = "footerVersionText";
			if (versionText.parentElement !== footer) {
				footer.appendChild(versionText);
			}
		};

		placeVersionText();
		const observer = new MutationObserver(placeVersionText);
		observer.observe(document.body, { childList: true, subtree: true });
		const intervalId = window.setInterval(placeVersionText, 1000);

		return () => {
			observer.disconnect();
			window.clearInterval(intervalId);
		};
	}, [location.pathname, homeLoaded]);

	const domain = process.env.VITE_DOMAIN || "akedo.gg";

	const structuredData = { "@context": "https://schema.org", "@type": "Games", "name": "Akedo Web Games", "url": `https://${domain}/`, "logo": `https://${domain}/logo.png`, "description": "Akedo | Free Web Games" };
	
	const checkTextInPathname=(pathstringtocheck)=> {
		return window.location.pathname.includes(pathstringtocheck);
	}
	
	return (
		<Suspense fallback={<>Loading...</>}>
		  	<Helmet> <script type="application/ld+json">{JSON.stringify(structuredData)}</script> </Helmet>
			{/* With no network the home data may never load; offline pages don't need it. */}
			{homeLoaded || !isOnline || location.pathname.startsWith("/offline") ? (
				<>
					{authLoading && <Loader isTranserent={true} />}
					{/* Offline, the site's own API calls fail; the header's Offline badge already says why. */}
					{isOnline && <Toast show={open} message={message} severity={severity} />}
					<AppRoutes /> 
					{OFFLINE_GAMES_ENABLED && <OfflineNotice />}
					{OFFLINE_GAMES_ENABLED && <OfflineDownloadPrompt />}
					{showMessagePopup && popupMessage && <MessagePopup show={showMessagePopup} message={popupMessage} dayCount={userMessagesList?.[0]?.data?.day || 1} />}
				</>
			) : (
				/*<Loader isTranserent={false} />*/
				<EngagingLoader type="bubbles" color={checkTextInPathname('category-custom/')?`#F6D14B`:`#00e6f4`}  />
			)}
		</Suspense>
	);
};

export default App;
